import { Input, List } from 'antd';
import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce/lib';
import { SurveyInstance } from '../../api/surveys/models';
import SurveysService from '../../api/surveys/SurveysService';
import { createBaseGridParams, getUrlQueryString, GridParams } from '../../helpers/grid-helpers';
import LoadingSpinner from '../Loading/LoadingSpinner';

const gridParams = createBaseGridParams({ sortField: 'title', pageSize: 6 });

interface Props {
  surveyTemplateId: number;
  completedOnly?: boolean;
  renderItem: (surveyInstance: SurveyInstance) => ReactNode;
}

const SurveyTestInstancesList: React.FC<Props> = ({
  surveyTemplateId,
  completedOnly,
  renderItem,
}) => {
  const { t } = useTranslation();
  const [isLoadingSurveyInstances, setIsLoadingSurveyInstances] = useState(false);
  const [surveyInstances, setSurveyInstances] = useState<SurveyInstance[]>([]);
  const [total, setTotal] = useState(0);

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebounce(searchInput, 500);
  const urlParams = getUrlQueryString();
  const [gridState, setGridState] = useState<GridParams>({
    ...gridParams,
    ...urlParams,
  });

  const handlePaginationChange = (pageNumber: number, pageSize: number | undefined) => {
    setGridState({ ...gridState, page: pageNumber - 1, pageSize: pageSize || 6 });
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        setIsLoadingSurveyInstances(true);
        let response = await SurveysService.findTestInstances(surveyTemplateId);
        response = completedOnly
          ? response.filter((surveyInstance) => surveyInstance.isCompleted)
          : response;
        setSurveyInstances(response);
        setTotal(response.length);
      } catch (error) {
      } finally {
        setIsLoadingSurveyInstances(false);
      }
    };
    fetch();
  }, [completedOnly, debouncedSearch, gridState, surveyTemplateId]);

  if (isLoadingSurveyInstances) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <Input.Search
        placeholder={`${t('Type to search a survey')}...`}
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        allowClear
        autoFocus
      />
      <List
        dataSource={surveyInstances}
        pagination={{
          current: gridState.page! + 1,
          total,
          pageSize: gridState.pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['6', '12', '24', '36', '48'],
          onChange: handlePaginationChange,
          onShowSizeChange: handlePaginationChange,
          showTotal: (totalRecords, rangeRecords) =>
            `${rangeRecords[0]}-${rangeRecords[1]} ${t('of')} ${totalRecords} ${t('items')}`,
        }}
        renderItem={renderItem}
      />
    </>
  );
};

export default SurveyTestInstancesList;
